/**
 * Recovering from an expired session.
 *
 * Tokens last an hour. Leave the console open longer than that and every request fails
 * with 401, which used to render as the word "Unauthorized" on an otherwise working-looking
 * page — the shell still showed because the stored user object existed, expired or not.
 *
 * The recovery is to sign in again, but that has to be done carefully: if the API keeps
 * rejecting us, blindly redirecting turns into an endless bounce between the console and
 * Cognito. Hence the attempt gate below.
 *
 * Pure so both rules are testable without a browser.
 */

/** Don't re-attempt sign-in more than once in this window. */
export const REAUTH_WINDOW_MS = 30_000;

/**
 * May we redirect to sign in again?
 *
 * False when we already tried moments ago, which means signing in did NOT fix the 401 —
 * showing the error is then more honest than looping forever.
 */
export function shouldAttemptReauth(
  lastAttemptMs: number | undefined,
  now: number,
  windowMs = REAUTH_WINDOW_MS
): boolean {
  if (lastAttemptMs === undefined) return true;
  if (!Number.isFinite(lastAttemptMs)) return true; // unreadable marker: don't get stuck
  // A marker from the future means a clock change; treat it as stale rather than blocking.
  if (lastAttemptMs > now) return true;
  return now - lastAttemptMs >= windowMs;
}

/**
 * Where to send the user after signing in again.
 *
 * Only ever an in-app path. The value survives a round trip through the identity provider,
 * so accepting an absolute URL would make it an open redirect: an attacker could send a
 * crafted link and have our own callback bounce the user to their site with the sign-in
 * looking legitimate.
 */
export function sanitizeReturnTo(raw: unknown, fallback = "/"): string {
  if (typeof raw !== "string" || raw.length === 0) return fallback;
  // Must be a rooted path, and must not be protocol-relative ("//evil.com").
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  // Reject anything that could be parsed as a scheme, and control characters that could
  // be used to smuggle one past this check.
  if (/[\u0000-\u001f\u007f]/.test(raw) || raw.includes("\\")) return fallback;
  return raw;
}

/** The path to come back to, captured before we leave the app. */
export function currentPath(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`;
}

/**
 * Should arriving here with no session go straight to sign-in, rather than showing the landing card?
 *
 * The admin portal links an operator directly to /r/:restaurantId/info. Tokens live in sessionStorage,
 * which is per ORIGIN, so arriving from admin.glowintech.app the console holds nothing — and it rendered
 * the "Sign in / Create an account" card. Two things were wrong with that. It asks for a decision from
 * somebody who has already made it, when Cognito's own session cookie would sign them straight back in
 * without a password. And `login()` there carries no state, so the destination was lost: you clicked a
 * restaurant and arrived at the picker.
 *
 * Only /r/ paths. Those are scoped to a restaurant and reachable only by someone who already has an
 * account, so there is nothing to explain to them. The root and /onboarding keep the card, because a
 * first-time visitor needs "create an account" more than they need a redirect.
 */
export function shouldResumeSignIn(pathname: string): boolean {
  return pathname === "/r" || pathname.startsWith("/r/");
}
